import { useCallback, useEffect, useRef, useState } from 'react';
import API_CONFIG from '../../../config/api';
import {
  formatDocumentText,
  getJiraInfo,
  normalizeStructuredSections,
  PKB_OPEN_SOURCE_REF_STORAGE_KEY,
} from '../productKnowledgeUtils';

const DEFAULT_LOAD_DOC_MESSAGE = 'Failed to load document';

/**
 * Parse JSON after an auth-aware fetch; throw if HTTP status is not OK or body is invalid JSON.
 * Aligns with API_CONFIG.request (returns raw Response, does not throw on 4xx/5xx).
 */
async function parseJsonOrThrow(response, defaultDetailMessage = DEFAULT_LOAD_DOC_MESSAGE) {
  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }
  if (!response.ok) {
    const detail = data?.detail || data?.message || `${defaultDetailMessage} (${response.status})`;
    throw new Error(detail);
  }
  return data;
}

const useProductKnowledgeDocument = ({
  selectedProject,
  staged,
  approved,
  setSnackbar,
}) => {
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [docContent, setDocContent] = useState({
    title: '',
    meta: null,
    sections: {},
    bodyMd: '',
    description: '',
    extraction: null,
    entryId: null,
    hasFigma: false,
    figmaFrames: [],
    images: [],
    imagesCount: 0,
    jiraKey: null,
    jiraUrl: null,
  });
  const [imageBlobUrls, setImageBlobUrls] = useState({});
  /** Monotonic id per openDocument invocation; stale async enrichment must not apply after a newer open. */
  const documentLoadSessionRef = useRef(0);

  const projectId = selectedProject?.project_id;

  const buildFallbackMeta = useCallback((item) => {
    const metadata = item?.content?.metadata;
    if (!metadata) return null;

    return {
      priority: metadata.priority,
      issueType: metadata.issue_type,
      status: metadata.status_name,
      assignee: metadata.assignee,
      labels: metadata.labels,
      subtasksCount: metadata.subtasks_count,
    };
  }, []);

  const openDocument = useCallback(async (item) => {
    try {
      if (!projectId) return;
      const loadSession = ++documentLoadSessionRef.current;
      const { jiraKey, jiraUrl } = getJiraInfo(item);
      const srdId = item?.content?.srd_id || null;
      const sessionEntryId = item.knowledge_id;

      if (srdId) {
        const res = await API_CONFIG.request(`/projects/srd/${projectId}/${encodeURIComponent(srdId)}`, { method: 'GET' });
        const json = await parseJsonOrThrow(res, DEFAULT_LOAD_DOC_MESSAGE);
        if (!json?.success) {
          throw new Error(json?.detail || DEFAULT_LOAD_DOC_MESSAGE);
        }

        setDocContent({
          title: formatDocumentText(json.meta?.title || 'Document'),
          meta: { ...(json.meta || {}), ...(buildFallbackMeta(item) || {}) },
          sections: normalizeStructuredSections(json.content?.sections || {}),
          bodyMd: json.content?.bodyMd || '',
          description: formatDocumentText(json.content?.description || ''),
          extraction: json.content?.extraction || null,
          entryId: item.knowledge_id,
          hasFigma: false,
          figmaFrames: [],
          images: [],
          imagesCount: 0,
          jiraKey,
          jiraUrl,
        });
        setDocDialogOpen(true);

        try {
          const pkbRes = await API_CONFIG.request(`/pkb/${projectId}/document/${item.knowledge_id}`, { method: 'GET' });
          if (!pkbRes.ok) return;
          const pkbJson = await pkbRes.json();
          if (pkbJson?.success) {
            setDocContent((prev) => {
              if (loadSession !== documentLoadSessionRef.current) return prev;
              if (prev.entryId !== sessionEntryId) return prev;
              return {
                ...prev,
                hasFigma: pkbJson.has_figma || prev.hasFigma,
                figmaFrames: pkbJson.figma_frames || prev.figmaFrames,
                images: pkbJson.images || [],
                imagesCount: pkbJson.images_count || (pkbJson.images || []).length,
                extraction: pkbJson.extraction || prev.extraction,
              };
            });
          }
        } catch {
          // Ignore enrichment failures.
        }
        return;
      }

      const res = await API_CONFIG.request(`/pkb/${projectId}/document/${item.knowledge_id}`, { method: 'GET' });
      const json = await parseJsonOrThrow(res, DEFAULT_LOAD_DOC_MESSAGE);
      if (!json?.success) {
        throw new Error(json?.detail || DEFAULT_LOAD_DOC_MESSAGE);
      }

      const fallbackDescription =
        json.markdown ||
        item?.content?.description ||
        item?.content?.content ||
        '';

      setDocContent({
        title: formatDocumentText(json.title || 'Document'),
        meta: buildFallbackMeta(item),
        sections: normalizeStructuredSections(json.sections || {}),
        inferredSections: json.inferred_sections || {},
        bodyMd: json.markdown || '',
        description: formatDocumentText(fallbackDescription),
        extraction: json.extraction || null,
        entryId: item.knowledge_id,
        hasFigma: json.has_figma || false,
        figmaFrames: json.figma_frames || [],
        images: json.images || [],
        imagesCount: json.images_count || (json.images || []).length,
        jiraKey,
        jiraUrl,
      });
      setDocDialogOpen(true);
    } catch (error) {
      setSnackbar({ open: true, message: error.message || DEFAULT_LOAD_DOC_MESSAGE, severity: 'error' });
    }
  }, [buildFallbackMeta, projectId, setSnackbar]);

  const closeDocument = useCallback(() => {
    setDocDialogOpen(false);
  }, []);

  useEffect(() => {
    let isActive = true;

    const fetchImages = async () => {
      if (!docDialogOpen || !Array.isArray(docContent.images)) return;
      const nextUrls = {};

      await Promise.all(
        docContent.images.map(async (image) => {
          if (!image?.file_url || !image?.id) return;
          try {
            const res = await API_CONFIG.request(image.file_url, { method: 'GET' });
            if (!res.ok) return;
            const blob = await res.blob();
            if (!isActive) return;
            nextUrls[image.id] = URL.createObjectURL(blob);
          } catch {
            // Ignore image load failures.
          }
        })
      );

      if (isActive) {
        setImageBlobUrls((prev) => {
          Object.values(prev).forEach((url) => URL.revokeObjectURL(url));
          return nextUrls;
        });
      }
    };

    fetchImages();
    return () => {
      isActive = false;
      setImageBlobUrls((prev) => {
        Object.values(prev).forEach((url) => URL.revokeObjectURL(url));
        return {};
      });
    };
  }, [docContent.images, docDialogOpen]);

  useEffect(() => {
    const raw = localStorage.getItem(PKB_OPEN_SOURCE_REF_STORAGE_KEY);
    if (!raw) return;
    if (!staged.length && !approved.length) return;

    // One-shot: clear storage after we attempt resolution so re-renders do not re-open.
    // If lists were empty above, we return early and leave storage intact for the next run.
    try {
      const sourceRef = JSON.parse(raw);
      const targetEntryId = sourceRef?.pkb_entry_id;
      const targetJiraKey = sourceRef?.jira_key;
      const allItems = [...staged, ...approved];
      const targetItem = allItems.find((item) => {
        if (targetEntryId && item.knowledge_id === targetEntryId) return true;
        const metadata = item?.content?.metadata || {};
        const itemKey = metadata.jira_issue_key || item?.jira_issue_key || item?.content?.external_key;
        return targetJiraKey && itemKey === targetJiraKey;
      });

      if (targetItem) {
        void openDocument(targetItem);
      } else {
        setSnackbar({ open: true, message: 'Linked Product Knowledge source was not found.', severity: 'warning' });
      }
    } catch {
      // Ignore malformed cross-tab payloads.
    } finally {
      localStorage.removeItem(PKB_OPEN_SOURCE_REF_STORAGE_KEY);
    }
  }, [approved, openDocument, setSnackbar, staged]);

  return {
    docDialogOpen,
    openDocument,
    closeDocument,
    docContent,
    setDocContent,
    imageBlobUrls,
  };
};

export default useProductKnowledgeDocument;
